import { difference, forEach, keys, union, uniq } from 'lodash-es';
import { UserRole } from '@/api/apis/user';
import { caseGroupByRole } from '@/constants/case/case.constants';
import { AssignedUser, AssignUserToGroupType, CaseUserGroup } from '@/api/apis/case';
import { groupsWithMandatoryPrimary, primaryAvailableGroups } from '@/constants/user.constants';
import { RoleKeyedPartial } from '@/types';

export const getMutationItems = (existingIds: string[] = [], newIds: string[] = []) => {
  const itemsToAttach = difference(newIds, existingIds);
  const itemsToDetach = difference(existingIds, newIds);
  return [itemsToAttach, itemsToDetach];
};

export const getPayloadItems = (
  idsToAttach: string[],
  idsToDetach: string[],
  group: CaseUserGroup,
  keyField: 'employeeId' | 'customerId',
  isDefaultAssignment = false,
) => {
  const isGroupWithDefaults = [CaseUserGroup.CustomerCoordinators, CaseUserGroup.CustomerReceivingSurgeons].includes(
    group,
  );
  const isGroupWithPrimary = primaryAvailableGroups.includes(group);
  const result: AssignUserToGroupType[] = [];
  const attachUnique = uniq(idsToAttach);
  const detachUnique = uniq(idsToDetach);

  forEach(attachUnique, (id, index) => {
    const isDefaultTxUser = isGroupWithDefaults && isDefaultAssignment && index === 0;
    result.push({
      action: 'assign',
      group,
      [keyField]: id,
      ...(isDefaultTxUser ? { isDefaultTxUser } : {}),
      ...(isGroupWithPrimary ? { isPrimary: false } : {}),
    });
  });
  forEach(detachUnique, (id) => {
    result.push({ action: 'unassign', group, [keyField]: id });
  });
  return result;
};

export const getUsersAssignmentPayload = (prev: RoleKeyedPartial<string[]>, next: RoleKeyedPartial<string[]>) => {
  const groupsWithDefaults = [CaseUserGroup.CustomerCoordinators, CaseUserGroup.CustomerReceivingSurgeons];
  const customerGroups = [
    CaseUserGroup.CustomerAdministrators,
    CaseUserGroup.CustomerTransplantSupporters,
    ...groupsWithDefaults,
  ];
  const result: AssignUserToGroupType[] = [];
  const allRoles = union(keys(prev), keys(next)) as UserRole[];
  forEach(allRoles, (role) => {
    const group = caseGroupByRole[role]!;
    const isDefaultGroup = groupsWithDefaults.includes(group);
    const keyField = customerGroups.includes(group) ? 'customerId' : 'employeeId';
    const [idsToAttach, idsToDetach] = getMutationItems(prev[role], next[role]);
    const isFullDetach = !difference(prev[role], idsToDetach).length;
    result.push(...getPayloadItems(idsToAttach, idsToDetach, group, keyField, isDefaultGroup && isFullDetach));
  });

  return result;
};

export const getShouldSetPrimary = (
  assignedEmployees: Partial<Record<CaseUserGroup, AssignedUser[]>> | undefined,
  groupsWithPrimaryAssigned: CaseUserGroup[],
  primaryUnassigned: boolean,
) =>
  groupsWithPrimaryAssigned.reduce((acc, group) => {
    const usersInGroup = assignedEmployees?.[group] ?? [];
    const hasNoPrimary = !usersInGroup.some((e) => e.isPrimary);
    const mandatoryPrimaryUnassigned = groupsWithMandatoryPrimary.includes(group) && primaryUnassigned;
    const shouldManuallySelectPrimary = hasNoPrimary || mandatoryPrimaryUnassigned;
    if (usersInGroup.length > 1 && shouldManuallySelectPrimary) {
      acc.push(group);
    }

    return acc;
  }, [] as CaseUserGroup[]).length > 0;
